import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Spin } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getAptYearlyStats, getAptStats, getAptIncomes, getAptExpenses,
  YearlyStats, MonthStats, AptStats, AptApartmentStat, AptChannelStat, AptIncome, AptExpense,
} from "../../../utils/api/apartmentRentalsAPI";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","Maj","Jun","Jul","Avg","Sep","Okt","Nov","Dec"];
const MONTH_FULL = ["Januar","Februar","Mart","April","Maj","Jun","Jul","Avgust","Septembar","Oktobar","Novembar","Decembar"];

const fmtAmount = (n: number, currency?: string) => {
  if (currency === "EUR") return `€${n >= 1000 ? `${Math.round(n / 1000)}k` : Math.round(n)}`;
  if (currency === "BAM") return `${n >= 1000 ? `${Math.round(n / 1000)}k` : Math.round(n)} KM`;
  // RSD default
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M RSD`;
  if (n >= 1000) return `${Math.round(n / 1000)}k RSD`;
  return `${Math.round(n)} RSD`;
};

const CHANNEL_LABEL: Record<string, string> = {
  direct: "Direktno",
  booking: "Booking.com",
  airbnb: "Airbnb",
  phone: "Telefon",
  other: "Ostalo",
};

const CHANNEL_COLOR: Record<string, string> = {
  direct:  "#16a34a",
  booking: "#5b50d6",
  airbnb:  "#dc2626",
  phone:   "#ca8a04",
  other:   "#9893a8",
};

const fmtNum = (n: number) => new Intl.NumberFormat("sr-RS").format(Math.round(n));

// ─── Bar chart ────────────────────────────────────────────────────────────────

interface BarChartProps {
  data: MonthStats[];
  metric: "revenue" | "occupancyRate" | "totalReservations";
  selectedMonth: number | null;
  onSelectMonth: (m: number) => void;
  currency?: string;
}

const BarChart: React.FC<BarChartProps> = ({ data, metric, selectedMonth, onSelectMonth, currency }) => {
  const values = data.map((d) => d[metric] as number);
  const max = Math.max(...values, 1);

  const fmtVal = (v: number) => {
    if (metric === "revenue") {
      if (currency === "EUR") return `€${fmtNum(v)}`;
      if (currency === "BAM") return `${fmtNum(v)} KM`;
      if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
      if (v >= 1000) return `${Math.round(v / 1000)}k`;
      return fmtNum(v);
    }
    if (metric === "occupancyRate") return `${v}%`;
    return String(v);
  };

  const barColor = (m: MonthStats) => {
    if (selectedMonth === m.month) return "#5b50d6";
    if (metric === "occupancyRate") {
      if (m.occupancyRate >= 80) return "#16a34a";
      if (m.occupancyRate >= 50) return "#ca8a04";
      return "#d1d5db";
    }
    return "#818cf8";
  };

  const now = new Date();
  const currentMonthIdx = now.getMonth(); // 0-based

  return (
    <div className="ar-chart">
      <div className="ar-chart-bars">
        {data.map((m) => {
          const val = m[metric] as number;
          const pct = max > 0 ? (val / max) * 100 : 0;
          const isCurrentMonth = m.month === now.getMonth() + 1;
          const isFuture = m.month > now.getMonth() + 1;

          return (
            <div
              key={m.month}
              className={`ar-chart-col${selectedMonth === m.month ? " ar-chart-col--active" : ""}${isFuture ? " ar-chart-col--future" : ""}`}
              onClick={() => onSelectMonth(m.month)}
            >
              <div className="ar-chart-val">{val > 0 ? fmtVal(val) : ""}</div>
              <div className="ar-chart-bar-wrap">
                <div
                  className="ar-chart-bar"
                  style={{
                    height: `${Math.max(pct, val > 0 ? 4 : 0)}%`,
                    background: isFuture ? "#e5e7eb" : barColor(m),
                  }}
                />
              </div>
              <div className={`ar-chart-label${isCurrentMonth ? " ar-chart-label--current" : ""}`}>
                {MONTH_SHORT[m.month - 1]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Month detail cards ───────────────────────────────────────────────────────

interface MonthDetailProps {
  stats: AptStats;
  month: number;
  year: number;
  extra: { income: number; expense: number };
}

const MonthDetail: React.FC<MonthDetailProps> = ({ stats, month, year, extra }) => {
  const netProfit = stats.revenue + extra.income - extra.expense;
  return (
    <div className="ar-stat-month-detail">
      <div className="ar-stat-kpi-grid">
        <div className="ar-stat-kpi ar-stat-kpi--featured">
          <div className="ar-stat-kpi-label">Popunjenost</div>
          <div className="ar-stat-kpi-val">
            {stats.occupancyRate}<span className="u">%</span>
          </div>
          <div className="ar-stat-kpi-bar">
            <div style={{ width: `${stats.occupancyRate}%` }} />
          </div>
          <div className="ar-stat-kpi-foot">{stats.occupiedNights} / {stats.totalNights} noći</div>
        </div>

        <div className="ar-stat-kpi">
          <div className="ar-stat-kpi-label">Prihod</div>
          <div className="ar-stat-kpi-val ar-stat-kpi-val--sm">{fmtNum(stats.revenue)}</div>
          <div className="ar-stat-kpi-foot">
            <span style={{ color: "#16a34a" }}>{fmtNum(stats.paidRevenue)} naplaćeno</span>
            {stats.pendingRevenue > 0 && (
              <span style={{ color: "#ca8a04", marginLeft: 8 }}>{fmtNum(stats.pendingRevenue)} pending</span>
            )}
          </div>
        </div>

        <div className="ar-stat-kpi">
          <div className="ar-stat-kpi-label">Rezervacije</div>
          <div className="ar-stat-kpi-val">{stats.totalReservations}</div>
          <div className="ar-stat-kpi-foot">potvrđenih rezervacija</div>
        </div>

        <div className="ar-stat-kpi">
          <div className="ar-stat-kpi-label">Slobodne noći</div>
          <div className="ar-stat-kpi-val">{stats.freeNights}</div>
          <div className="ar-stat-kpi-foot">od ukupno {stats.totalNights}</div>
        </div>

        <div className="ar-stat-kpi">
          <div className="ar-stat-kpi-label">Dodatni prihod / Rashod</div>
          <div className="ar-stat-kpi-val ar-stat-kpi-val--sm">
            <span style={{ color: "#16a34a" }}>+{fmtNum(extra.income)}</span>
            {" / "}
            <span style={{ color: "#dc2626" }}>-{fmtNum(extra.expense)}</span>
          </div>
          <div className="ar-stat-kpi-foot">iz taba Finansije</div>
        </div>

        <div className="ar-stat-kpi">
          <div className="ar-stat-kpi-label">Realna zarada</div>
          <div className="ar-stat-kpi-val ar-stat-kpi-val--sm" style={{ color: netProfit >= 0 ? "#16a34a" : "#dc2626" }}>
            {netProfit >= 0 ? "+" : ""}{fmtNum(netProfit)}
          </div>
          <div className="ar-stat-kpi-foot">prihodi + dodatni prihod − rashodi</div>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

interface StatsTabProps {
  clientId: string;
}

const StatsTab: React.FC<StatsTabProps> = ({ clientId }) => {
  const now = new Date();
  const [statYear, setStatYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(now.getMonth() + 1);
  const [metric, setMetric] = useState<"revenue" | "occupancyRate" | "totalReservations">("revenue");

  const [yearlyStats, setYearlyStats] = useState<YearlyStats | null>(null);
  const [monthStats, setMonthStats] = useState<AptStats | null>(null);
  const [yearlyLoading, setYearlyLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [yearIncomes, setYearIncomes] = useState<AptIncome[]>([]);
  const [yearExpenses, setYearExpenses] = useState<AptExpense[]>([]);

  // Load yearly data
  const loadYearly = useCallback(async () => {
    if (!clientId) return;
    setYearlyLoading(true);
    try {
      const res = await getAptYearlyStats(clientId, statYear);
      setYearlyStats(res.data);
    } catch { /**/ } finally {
      setYearlyLoading(false);
    }
  }, [clientId, statYear]);

  // Load selected month detail
  const loadMonth = useCallback(async () => {
    if (!clientId || !selectedMonth) return;
    setMonthLoading(true);
    try {
      const res = await getAptStats(clientId, statYear, selectedMonth);
      setMonthStats(res.data);
    } catch { /**/ } finally {
      setMonthLoading(false);
    }
  }, [clientId, statYear, selectedMonth]);

  // Load additional income/expenses for the whole year (for "Realna zarada")
  const loadYearlyFinance = useCallback(async () => {
    if (!clientId) return;
    try {
      const from = dayjs(new Date(statYear, 0, 1)).startOf("day").toISOString();
      const to = dayjs(new Date(statYear, 11, 31)).endOf("day").toISOString();
      const [incRes, expRes] = await Promise.all([
        getAptIncomes(clientId, { from, to }),
        getAptExpenses(clientId, { from, to }),
      ]);
      setYearIncomes(incRes.data);
      setYearExpenses(expRes.data);
    } catch { /**/ }
  }, [clientId, statYear]);

  useEffect(() => { loadYearly(); }, [loadYearly]);
  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadYearlyFinance(); }, [loadYearlyFinance]);

  // Additional income/expense bucketed per month (1-12)
  const extraByMonth = useMemo(() => {
    const map: Record<number, { income: number; expense: number }> = {};
    for (let m = 1; m <= 12; m++) map[m] = { income: 0, expense: 0 };
    yearIncomes.forEach((i) => { map[dayjs(i.date).month() + 1].income += i.amount || 0; });
    yearExpenses.forEach((e) => { map[dayjs(e.date).month() + 1].expense += e.amount || 0; });
    return map;
  }, [yearIncomes, yearExpenses]);

  // Year totals
  const yearTotals = yearlyStats ? {
    revenue: yearlyStats.months.reduce((s, m) => s + m.revenue, 0),
    reservations: yearlyStats.months.reduce((s, m) => s + m.totalReservations, 0),
    avgOccupancy: Math.round(yearlyStats.months.reduce((s, m) => s + m.occupancyRate, 0) / 12),
    paidRevenue: yearlyStats.months.reduce((s, m) => s + m.paidRevenue, 0),
    additionalIncome: Object.values(extraByMonth).reduce((s, v) => s + v.income, 0),
    additionalExpense: Object.values(extraByMonth).reduce((s, v) => s + v.expense, 0),
  } : null;

  const metricOptions: { key: typeof metric; label: string }[] = [
    { key: "revenue", label: "Prihod" },
    { key: "occupancyRate", label: "Popunjenost" },
    { key: "totalReservations", label: "Rezervacije" },
  ];

  return (
    <div className="ar-stat-page">
      {/* Year navigator */}
      <div className="ar-stat-head">
        <div className="ar-stat-year-nav">
          <button className="ar-step" onClick={() => setStatYear((y) => y - 1)}>
            <LeftOutlined />
          </button>
          <span className="ar-stat-year">{statYear}</span>
          <button
            className="ar-step"
            onClick={() => setStatYear((y) => y + 1)}
            disabled={statYear >= now.getFullYear()}
          >
            <RightOutlined />
          </button>
        </div>

        {/* Metric switcher */}
        <div className="ar-stat-metric-sw">
          {metricOptions.map((o) => (
            <button
              key={o.key}
              className={`ar-stat-metric-btn${metric === o.key ? " active" : ""}`}
              onClick={() => setMetric(o.key)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Year summary KPIs */}
      {yearTotals && !yearlyLoading && (
        <div className="ar-stat-year-kpis">
          <div className="ar-stat-year-kpi">
            <div className="l">Ukupan prihod {statYear}</div>
            <div className="v">{fmtNum(yearTotals.revenue)}</div>
            <div className="s">{fmtNum(yearTotals.paidRevenue)} naplaćeno</div>
          </div>
          <div className="ar-stat-year-kpi">
            <div className="l">Ukupno rezervacija</div>
            <div className="v">{yearTotals.reservations}</div>
            <div className="s">za godinu {statYear}</div>
          </div>
          <div className="ar-stat-year-kpi">
            <div className="l">Prosj. popunjenost</div>
            <div className="v">{yearTotals.avgOccupancy}<span style={{ fontSize: 18, fontWeight: 400 }}>%</span></div>
            <div className="s">godišnji prosjek</div>
          </div>
          <div className="ar-stat-year-kpi">
            <div className="l">Apartmana</div>
            <div className="v">{yearlyStats?.apartments}</div>
            <div className="s">aktivnih u sistemu</div>
          </div>
          <div className="ar-stat-year-kpi">
            <div className="l">Dodatni prihod / Rashod</div>
            <div className="v" style={{ fontSize: 18 }}>
              <span style={{ color: "#16a34a" }}>+{fmtNum(yearTotals.additionalIncome)}</span>
              {" / "}
              <span style={{ color: "#dc2626" }}>-{fmtNum(yearTotals.additionalExpense)}</span>
            </div>
            <div className="s">za godinu {statYear}</div>
          </div>
          <div className="ar-stat-year-kpi">
            <div className="l">Realna zarada {statYear}</div>
            {(() => {
              const netProfit = yearTotals.revenue + yearTotals.additionalIncome - yearTotals.additionalExpense;
              return (
                <div className="v" style={{ color: netProfit >= 0 ? "#16a34a" : "#dc2626" }}>
                  {netProfit >= 0 ? "+" : ""}{fmtNum(netProfit)}
                </div>
              );
            })()}
            <div className="s">prihodi + dodatni prihod − rashodi</div>
          </div>
        </div>
      )}

      {/* Bar chart */}
      <div className="ar-stat-chart-wrap">
        <div className="ar-stat-chart-title">
          {metricOptions.find((o) => o.key === metric)?.label} po mjesecu · {statYear}
          <span className="ar-stat-chart-hint">Kliknite na bar za detalje</span>
        </div>
        {yearlyLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : yearlyStats ? (
          <BarChart
            data={yearlyStats.months}
            metric={metric}
            selectedMonth={selectedMonth}
            onSelectMonth={setSelectedMonth}
          />
        ) : null}
      </div>

      {/* Monthly detail */}
      <div className="ar-stat-month-wrap">
        <div className="ar-stat-month-head">
          <div className="ar-stat-month-title">
            <button
              className="ar-step"
              onClick={() => {
                if (selectedMonth === 1) { setSelectedMonth(12); setStatYear((y) => y - 1); }
                else setSelectedMonth((m) => m - 1);
              }}
            ><LeftOutlined /></button>
            {MONTH_FULL[selectedMonth - 1]} {statYear}
            <button
              className="ar-step"
              onClick={() => {
                if (selectedMonth === 12) { setSelectedMonth(1); setStatYear((y) => y + 1); }
                else setSelectedMonth((m) => m + 1);
              }}
              disabled={statYear > now.getFullYear() || (statYear === now.getFullYear() && selectedMonth >= 12)}
            ><RightOutlined /></button>
          </div>
        </div>

        {monthLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <Spin />
          </div>
        ) : monthStats ? (
          <MonthDetail stats={monthStats} month={selectedMonth} year={statYear} extra={extraByMonth[selectedMonth]} />
        ) : null}
      </div>

      {/* Monthly table */}
      {yearlyStats && !yearlyLoading && (
        <div className="ar-stat-table-wrap">
          <table className="ar-stat-table">
            <thead>
              <tr>
                <th>Mjesec</th>
                <th>Rezerv.</th>
                <th>Noći</th>
                <th>Popunjenost</th>
                <th>Prihod</th>
                <th>Naplaćeno</th>
                <th>Pending</th>
                <th>Dodatni prihod</th>
                <th>Rashod</th>
                <th>Realna zarada</th>
              </tr>
            </thead>
            <tbody>
              {yearlyStats.months.map((m) => {
                const isCurrent = m.month === now.getMonth() + 1 && statYear === now.getFullYear();
                const isSel = m.month === selectedMonth;
                const isFuture = statYear === now.getFullYear() && m.month > now.getMonth() + 1;
                const extra = extraByMonth[m.month];
                const netProfit = m.revenue + extra.income - extra.expense;
                return (
                  <tr
                    key={m.month}
                    className={`${isSel ? "ar-stat-tr--selected" : ""}${isFuture ? " ar-stat-tr--future" : ""}`}
                    onClick={() => setSelectedMonth(m.month)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span style={{ fontWeight: isCurrent ? 700 : 500 }}>
                        {MONTH_FULL[m.month - 1]}
                        {isCurrent && <span className="ar-stat-now-pill">Ovaj mj.</span>}
                      </span>
                    </td>
                    <td>{m.totalReservations}</td>
                    <td>{m.occupiedNights}</td>
                    <td>
                      <div className="ar-stat-occ-cell">
                        <div className="ar-stat-occ-bar">
                          <div style={{
                            width: `${m.occupancyRate}%`,
                            background: m.occupancyRate >= 80 ? "#16a34a" : m.occupancyRate >= 50 ? "#ca8a04" : "#d1d5db",
                          }} />
                        </div>
                        {m.occupancyRate}%
                      </div>
                    </td>
                    <td className="ar-stat-td-num">{fmtNum(m.revenue)}</td>
                    <td className="ar-stat-td-num" style={{ color: "#16a34a" }}>{fmtNum(m.paidRevenue)}</td>
                    <td className="ar-stat-td-num" style={{ color: m.pendingRevenue > 0 ? "#ca8a04" : "#9ca3af" }}>
                      {fmtNum(m.pendingRevenue)}
                    </td>
                    <td className="ar-stat-td-num" style={{ color: "#16a34a" }}>{fmtNum(extra.income)}</td>
                    <td className="ar-stat-td-num" style={{ color: "#dc2626" }}>{fmtNum(extra.expense)}</td>
                    <td className="ar-stat-td-num" style={{ color: netProfit >= 0 ? "#16a34a" : "#dc2626" }}>
                      <strong>{netProfit >= 0 ? "+" : ""}{fmtNum(netProfit)}</strong>
                    </td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr className="ar-stat-tr--total">
                <td><strong>Ukupno {statYear}</strong></td>
                <td><strong>{yearTotals?.reservations}</strong></td>
                <td><strong>{yearlyStats.months.reduce((s, m) => s + m.occupiedNights, 0)}</strong></td>
                <td><strong>{yearTotals?.avgOccupancy}%</strong></td>
                <td className="ar-stat-td-num"><strong>{fmtNum(yearTotals?.revenue ?? 0)}</strong></td>
                <td className="ar-stat-td-num" style={{ color: "#16a34a" }}><strong>{fmtNum(yearTotals?.paidRevenue ?? 0)}</strong></td>
                <td className="ar-stat-td-num" style={{ color: "#ca8a04" }}>
                  <strong>{fmtNum((yearTotals?.revenue ?? 0) - (yearTotals?.paidRevenue ?? 0))}</strong>
                </td>
                <td className="ar-stat-td-num" style={{ color: "#16a34a" }}><strong>{fmtNum(yearTotals?.additionalIncome ?? 0)}</strong></td>
                <td className="ar-stat-td-num" style={{ color: "#dc2626" }}><strong>{fmtNum(yearTotals?.additionalExpense ?? 0)}</strong></td>
                <td className="ar-stat-td-num">
                  {(() => {
                    const net = (yearTotals?.revenue ?? 0) + (yearTotals?.additionalIncome ?? 0) - (yearTotals?.additionalExpense ?? 0);
                    return (
                      <strong style={{ color: net >= 0 ? "#16a34a" : "#dc2626" }}>{net >= 0 ? "+" : ""}{fmtNum(net)}</strong>
                    );
                  })()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ─ Channels + Top Apartments (from selected month stats) ─ */}
      {monthStats && !monthLoading && (
        <div className="ar-stat-section-grid">

          {/* Channels breakdown */}
          {monthStats.channels && monthStats.channels.length > 0 && (
            <div className="ar-stat-section-card">
              <div className="ar-stat-section-card-title">
                Po kanalima · {MONTH_SHORT[selectedMonth - 1]}
                <span style={{ color: "#9893a8", fontWeight: 500 }}>
                  {monthStats.channels.reduce((s, c) => s + c.count, 0)} rezerv.
                </span>
              </div>
              <div className="ar-stat-channels">
                {(() => {
                  const maxCount = Math.max(...monthStats.channels!.map((c) => c.count), 1);
                  return monthStats.channels!.map((c) => (
                    <div key={c.channel} className="ar-stat-channel-row">
                      <span
                        className="dot"
                        style={{ background: CHANNEL_COLOR[c.channel] || "#9893a8" }}
                      />
                      <span className="name">{CHANNEL_LABEL[c.channel] || c.channel}</span>
                      <div className="bar-wrap">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${(c.count / maxCount) * 100}%`,
                            background: CHANNEL_COLOR[c.channel] || "#9893a8",
                          }}
                        />
                      </div>
                      <span className="val">{c.count}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Top apartmani */}
          {monthStats.apartmentStats && monthStats.apartmentStats.length > 0 && (
            <div className="ar-stat-section-card">
              <div className="ar-stat-section-card-title">
                Top apartmani · {MONTH_SHORT[selectedMonth - 1]}
              </div>

              {/* ADR mini bars */}
              {monthStats.apartmentStats.some((a) => a.adr > 0) && (
                <div className="ar-stat-adr-bars" style={{ marginBottom: 16 }}>
                  {(() => {
                    const maxAdr = Math.max(...monthStats.apartmentStats!.map((a) => a.adr), 1);
                    return monthStats.apartmentStats!.slice(0, 5).map((a) => (
                      <div key={a.id} className="adr-col">
                        <span className="bar-val">{fmtAmount(a.adr, a.currency)}</span>
                        <div
                          className="bar"
                          style={{ height: `${Math.max((a.adr / maxAdr) * 100, 8)}%` }}
                        />
                        <span className="bar-name" title={a.name}>
                          {a.name.split(" ")[0]}
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              )}

              <div className="ar-stat-rank-list">
                {monthStats.apartmentStats.map((a, i) => (
                  <div key={a.id} className="ar-stat-rank-row">
                    <span className="num">{i + 1}</span>
                    <div className="info">
                      <div className="name">{a.name}</div>
                      <div className="meta">
                        {a.reservations} rez. · {a.occupiedNights} noći · {a.occupancyRate}% popunj.
                      </div>
                    </div>
                    <div className="rev">
                      {fmtAmount(a.revenue, a.currency)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default StatsTab;
